import {HubConnectionBuilder, HubConnection, HttpTransportType} from '@microsoft/signalr';

import {CookieManager} from 'Core/Common/CookieManager';
import {Notifier, MessageTypes} from 'Core/Common/Notifier';
import {P} from 'Core/Common/Promise';
import * as $ from 'jquery';
import {NotificationTypes} from '../../Common/Enums/NotificationTypes';
import {PUB_SUB_EVENTS} from 'MenuManager/PubSubEvents';

import { FunctionBuilder } from 'Core/Components/CustomFunctions/FunctionBuilder';
import { BlockUI } from "Core/Common/BlockUi";
import { Guid } from '../../Common/Guid';
import {Event} from 'Core/Common/Event'

export class SignalRNotificationManager extends Event {
    private _signalRHub: HubConnection;

    private static instance: SignalRNotificationManager;
    private static allowInstantiation: boolean;
    private _debugEditor: any;
    private _scriptDebugEditor: any;
    public ImageCache: any = {};

    constructor() {
        super();
        if (!SignalRNotificationManager.allowInstantiation) {
            throw new Error('Use SignalRNotificationManager.Instance instead');
        }
        this.Init();
    }

    public async EvaluateFunctionResponse(result: string, error: string, objectId: string){
        await this._signalRHub.send('EvaluateFunctionResponse', result, error, objectId);
    };
    

    public SetScriptDebugEditor(debugEditor) {
        this._scriptDebugEditor = debugEditor;
        if (this._scriptDebugEditor) {
            this._scriptDebugEditor.session.setMode('ace/mode/json');
            this._scriptDebugEditor.session.setUseWorker(false);
        }
    }

    public SetDebugEditor(debugEditor) {
        this._debugEditor = debugEditor;
        if (this._debugEditor) {
            this._debugEditor.session.setMode('ace/mode/json');
            this._debugEditor.session.setUseWorker(false);
        }
    }

    private Init() {
        this.AddEvent('PASTE_IMAGE');
        this.AddEvent('UPDATE_AGENDA');
        this.AddEvent('WARNING_NOTIFICATION');
        this.AddEvent('EXPORT_PROGRESS');
        this._signalRHub = new HubConnectionBuilder()
            .withUrl('/api/signalRHub', {accessTokenFactory: () => CookieManager.GetAuthToken(), skipNegotiation: true, transport: HttpTransportType.WebSockets})
            .withAutomaticReconnect()
            .build();

        this._signalRHub.on('ShowDebugLog', (message) => {
            if (this._debugEditor) {
                this._debugEditor.session.insert({
                    row: this._debugEditor.session.getLength(),
                    column: 0
                }, "\n" + message);

                this._debugEditor.scrollToLine(this._debugEditor.session.getLength(), true, true, function () {
                });
                this._debugEditor.gotoLine(this._debugEditor.session.getLength(), 0, true);
            }
        });

        this._signalRHub.on('ConsoleLog', (message) => {
            if (this._scriptDebugEditor) {
                this._scriptDebugEditor.session.insert({
                    row: this._scriptDebugEditor.session.getLength(),
                    column: 0
                }, "\n" + message);

                this._scriptDebugEditor.scrollToLine(this._scriptDebugEditor.session.getLength(), true, true, function () {});
                this._scriptDebugEditor.gotoLine(this._scriptDebugEditor.session.getLength(), 0, true);
            }
        });

        this._signalRHub.on('ConsoleImage', (title, image) => this.Trigger('PASTE_IMAGE', { title: title, image: image }));

        this._signalRHub.on('ShowProgress', message => {
            if (message.startsWith('ERROR:')) {
                // Parse error message: ERROR:tableName:errorMessage
                const parts = message.substring(6).split(':');
                const tableName = parts[0];
                const errorMessage = parts.slice(1).join(':');
                const errorHtml = `<div class="alert alert-danger" style="margin: 5px 0; padding: 8px; font-size: 12px;"><strong>${tableName}:</strong> ${errorMessage}</div>`;
                $("#dw-errors").append(errorHtml).show();
            } else {
                $("#dw-progress").html(`<h2>${message}</h2>`);
            }
        });

        this._signalRHub.on('ExportGridProgress', (progress: { exported: number }) => {
            this.Trigger('EXPORT_PROGRESS', { Exported: progress.exported });
        });

        this._signalRHub.on('UpdateAgenda',  (id: number, operation: string) => {
            this.Trigger('UPDATE_AGENDA', { id: id, operation: operation })
        });

        this._signalRHub.on('ShowNotification', (message, notificationType: NotificationTypes) => {
            if (notificationType === NotificationTypes.Warning) {
                this.Trigger('WARNING_NOTIFICATION', { message });
                const $warnings = $('#dw-warnings');
                if ($warnings.length) {
                    const escaped = $('<div/>').text(message).html();
                    const warningHtml = `<div class="note note-warning note-bordered" style="white-space: pre-wrap; font-family: Consolas, 'Courier New', monospace; font-size: 13px; line-height: 1.55;">${escaped}</div>`;
                    $warnings.append(warningHtml).show();
                } else {
                    new Notifier(null).Warning(message);
                }
            } else if (notificationType === NotificationTypes.Failed) {
                new Notifier(null).Failed(message);
            } else {
                new Notifier(null).Success(message);
            }
        });

        this._signalRHub.on('ShowPersistentNotification', (message, notificationType: NotificationTypes) => {
            if (notificationType === NotificationTypes.Warning) {
                new Notifier(null).PersistentWarning(message);
            } else if (notificationType === NotificationTypes.Failed) {
                new Notifier(null).PersistentFailed(message);
            } else {
                new Notifier(null).PersistentSuccess(message);
            }
        });

        this._signalRHub.on('EvaluateFunction', (code, params, objectId) => {
            if (objectId) {
                PubSub.publishSync(PUB_SUB_EVENTS.EXECUTE_SCRIPT, { Code: code, Params: params, ObjectId: objectId });
                return;
            }
            this.EvalFunction(code, params);
        });

        this._signalRHub.on('ShowMessage', message => {
            new Notifier(null).Show({ Message: message, Type: new MessageTypes().Success, ZIndex: 1000000 });
        });
    }

    private SkipRows(skipValue: number){
        for(var indx = 0; indx<skipValue;indx++){
            this._scriptDebugEditor.session.insert({
                row: this._scriptDebugEditor.session.getLength(),
                column: 0
            }, "\n");

            this._scriptDebugEditor.scrollToLine(this._scriptDebugEditor.session.getLength(), true, true, function () {
            });
            this._scriptDebugEditor.gotoLine(this._scriptDebugEditor.session.getLength(), 0, true);
        }
    }

	private EvalFunction(code, param) {
		(async ()=> { await FunctionBuilder.Execute(code, param) })();
	}

    static get Instance(): SignalRNotificationManager {
        if (SignalRNotificationManager.instance == null) {
            SignalRNotificationManager.allowInstantiation = true;
            SignalRNotificationManager.instance = new SignalRNotificationManager();
            SignalRNotificationManager.allowInstantiation = false;
        }

        return SignalRNotificationManager.instance;
    }

    SetToken() {
        const token = CookieManager.GetAuthToken();
        $.ajaxSetup({headers: {"Authorization": `Bearer ${token}`}});
    }

    Start(): P.Promise<any> {
        let deferredResult = P.defer<any>();
        this.Stop().finally(()=>{
            BlockUI.Block();

            Promise
                .all([
                    this._signalRHub.start()
                ])
                .then(() => {
                    BlockUI.Unblock();
                    deferredResult.resolve(null);
                });
        });
        return deferredResult.promise();
    }

    Stop() {
        return this._signalRHub.stop();
    }
}